/**
 * Chapter definitions for Cells & Life
 * Chapters 1-2: Discovering Cells and One or Many?
 */

import type { Chapter, Level, BossChallenge } from '@/types';

// ============================================================================
// CHAPTER 1: DISCOVERING CELLS
// ============================================================================

const LEVEL_1_1: Level = {
  id: '1-1',
  name: 'First Look',
  type: 'discovery',
  specimens: ['bacterium_basic', 'bacterium_rod', 'bacterium_spiral', 'dust_particle', 'water_bubble'],
  objectives: [
    { type: 'find_cells', count: 3 },
    { type: 'identify_nonliving', count: 2 },
  ],
  tutorialElements: ['zoom_control', 'pan_control', 'scanner_intro'],
};

const LEVEL_1_2: Level = {
  id: '1-2',
  name: 'Cell Shapes',
  type: 'discovery',
  specimens: ['bacterium_basic', 'bacterium_rod', 'bacterium_spiral'],
  objectives: [
    { type: 'catalog_cells', count: 3 },
  ],
  tutorialElements: ['cell_journal'],
};

const LEVEL_1_3: Level = {
  id: '1-3',
  name: 'Living or Not?',
  type: 'discovery',
  specimens: ['amoeba', 'crystal', 'paramecium', 'dust_particle'],
  objectives: [
    { type: 'classify_correctly', accuracy: 0.8 },
  ],
  tutorialElements: ['cell_journal', 'evidence_cards'],
};

const CHAPTER_1_BOSS: BossChallenge = {
  name: 'The Mystery Sample',
  description: 'Can you identify these tricky specimens? Some look alive but aren\'t, and some look dead but are actually living!',
  specimens: ['virus_particle', 'dormant_bacterium', 'pollen_grain', 'oil_droplet'],
  difficulty: 'medium',
};

export const CHAPTER_1: Chapter = {
  id: 'ch1_discovering_cells',
  title: 'Discovering Cells',
  learningObjectives: [
    'Understand that living things are made of cells',
    'Learn to identify cells under a microscope',
    'Distinguish cells from non-living particles',
  ],
  levels: [LEVEL_1_1, LEVEL_1_2, LEVEL_1_3],
  bossChallenge: CHAPTER_1_BOSS,
};

// ============================================================================
// CHAPTER 2: ONE OR MANY?
// ============================================================================

const LEVEL_2_1: Level = {
  id: '2-1',
  name: 'Single Cell Life',
  type: 'simulation',
  specimens: ['amoeba', 'paramecium', 'euglena'],
  objectives: [
    { type: 'observe_function', functions: ['metabolism', 'response'] },
  ],
  focus: ['metabolism', 'response_to_stimuli'],
  mechanics: ['watch_function', 'identify_organelles'],
};

const LEVEL_2_2: Level = {
  id: '2-2',
  name: 'Many Cells Together',
  type: 'discovery',
  specimens: ['plant_leaf_section', 'animal_skin_cells'],
  objectives: [
    { type: 'count_cells', threshold: 10 },
    { type: 'identify_tissue_type', count: 2 },
  ],
  focus: ['cell_organization', 'specialization'],
};

const LEVEL_2_3: Level = {
  id: '2-3',
  name: 'Classification Challenge',
  type: 'classification',
  specimens: ['bacterium_basic', 'amoeba', 'paramecium', 'plant_leaf_section', 'animal_skin_cells'],
  objectives: [
    { type: 'sort_unicellular_multicellular', accuracy: 0.9 },
  ],
};

const CHAPTER_2_BOSS: BossChallenge = {
  name: 'The Colony Question',
  description: 'Some organisms live in groups but each cell is independent. Are they unicellular or multicellular?',
  specimens: ['bacterium_basic'], // Colony of bacteria
  challenges: [
    'Is a bacterial colony unicellular or multicellular?',
    'What about a slime mold?',
    'How do cells in multicellular organisms differ from cells in a colony?',
  ],
  difficulty: 'hard',
};

export const CHAPTER_2: Chapter = {
  id: 'ch2_one_or_many',
  title: 'One or Many?',
  learningObjectives: [
    'Distinguish unicellular from multicellular organisms',
    'Understand that single cells perform all life functions',
    'Recognize cell diversity in multicellular organisms',
  ],
  levels: [LEVEL_2_1, LEVEL_2_2, LEVEL_2_3],
  unlockRequirement: {
    type: 'chapter_complete',
    targetId: 'ch1_discovering_cells',
  },
  bossChallenge: CHAPTER_2_BOSS,
};

// ============================================================================
// CHAPTER 3: WHAT MAKES LIFE?
// ============================================================================

const LEVEL_3_1: Level = {
  id: '3-1',
  name: 'Energy & Growth',
  type: 'simulation',
  specimens: ['amoeba', 'paramecium'],
  objectives: [
    { type: 'observe_function', functions: ['metabolism', 'growth'] },
  ],
  focus: ['metabolism', 'growth'],
  mechanics: ['feed_cell', 'observe_growth', 'measure_energy'],
};

const LEVEL_3_2: Level = {
  id: '3-2',
  name: 'Response & Balance',
  type: 'simulation',
  specimens: ['paramecium', 'euglena'],
  objectives: [
    { type: 'observe_function', functions: ['response', 'homeostasis'] },
  ],
  focus: ['response_to_stimuli', 'homeostasis'],
  mechanics: ['apply_stimulus', 'observe_response', 'track_equilibrium'],
};

const LEVEL_3_3: Level = {
  id: '3-3',
  name: 'Reproduction',
  type: 'simulation',
  specimens: ['amoeba', 'bacterium_basic'],
  objectives: [
    { type: 'observe_function', functions: ['reproduction'] },
  ],
  focus: ['reproduction'],
  mechanics: ['healthy_cell_division', 'observe_stages'],
};

const LEVEL_3_4: Level = {
  id: '3-4',
  name: 'Life Function Master',
  type: 'classification',
  specimens: ['amoeba', 'paramecium', 'bacterium_basic', 'crystal', 'water_bubble'],
  objectives: [
    { type: 'identify_all_functions', accuracy: 1.0 },
  ],
};

const CHAPTER_3_BOSS: BossChallenge = {
  name: 'The Life Test',
  description: 'Can you identify all the life functions in these organisms? Some specimens might trick you!',
  specimens: ['dormant_bacterium', 'virus_particle', 'amoeba'],
  challenges: [
    'Which specimens show metabolism?',
    'Can a dormant cell still be alive?',
    'Why is a virus not considered living?',
  ],
  difficulty: 'hard',
};

export const CHAPTER_3: Chapter = {
  id: 'ch3_life_functions',
  title: 'What Makes Life?',
  learningObjectives: [
    'Identify essential functions of life',
    'Observe life functions in unicellular organisms',
    'Understand homeostasis at the cellular level',
    'Recognize all 7 life functions',
  ],
  levels: [LEVEL_3_1, LEVEL_3_2, LEVEL_3_3, LEVEL_3_4],
  unlockRequirement: {
    type: 'chapter_complete',
    targetId: 'ch2_one_or_many',
  },
  bossChallenge: CHAPTER_3_BOSS,
};

// ============================================================================
// CHAPTER 4: CELL THEORY
// ============================================================================

const LEVEL_4_1: Level = {
  id: '4-1',
  name: 'All Life is Cellular',
  type: 'discovery',
  specimens: ['bacterium_basic', 'amoeba', 'plant_leaf_section', 'animal_skin_cells', 'paramecium'],
  objectives: [
    { type: 'verify_cell_presence', count: 10 },
    { type: 'find_counterexample', expect: 'none' },
  ],
  narrative: 'historical',
  tutorialElements: ['cell_theory_intro', 'historical_context'],
};

const LEVEL_4_2: Level = {
  id: '4-2',
  name: 'Cells from Cells',
  type: 'simulation',
  specimens: ['amoeba', 'bacterium_basic', 'paramecium'],
  objectives: [
    { type: 'observe_function', functions: ['reproduction'] },
    { type: 'trace_cell_lineage', generations: 3 },
  ],
  focus: ['cell_division', 'lineage'],
  mechanics: ['trace_ancestry', 'observe_division', 'watch_binary_fission'],
};

const LEVEL_4_3: Level = {
  id: '4-3',
  name: 'The Basic Unit',
  type: 'discovery',
  specimens: ['amoeba', 'bacterium_basic', 'plant_leaf_section'],
  objectives: [
    { type: 'identify_smallest_living', count: 3 },
    { type: 'zoom_beyond_cell', observe: 'molecules_not_alive' },
  ],
  focus: ['smallest_living_unit'],
  mechanics: ['zoom_to_cell', 'zoom_further_not_living'],
};

const CHAPTER_4_BOSS: BossChallenge = {
  name: 'Defend Cell Theory',
  description: 'Can you defend cell theory against challenging questions? Some things seem to break the rules!',
  specimens: ['virus_particle', 'dormant_bacterium', 'prion_particle', 'mitochondria_isolated'],
  challenges: [
    'What about viruses? Are they living cells?',
    'Can life arise spontaneously from non-living matter?',
    'What is the smallest living thing?',
    'If mitochondria have their own DNA, are they separate cells?',
  ],
  difficulty: 'hard',
};

export const CHAPTER_4: Chapter = {
  id: 'ch4_cell_theory',
  title: 'Cell Theory',
  learningObjectives: [
    'Understand the three tenets of cell theory',
    'Learn the historical development of cell theory',
    'Apply cell theory to explain observations',
    'Recognize exceptions and edge cases',
  ],
  levels: [LEVEL_4_1, LEVEL_4_2, LEVEL_4_3],
  unlockRequirement: {
    type: 'chapter_complete',
    targetId: 'ch3_life_functions',
  },
  bossChallenge: CHAPTER_4_BOSS,
};

// ============================================================================
// CHAPTER 5: BUILDING LIFE
// ============================================================================

const LEVEL_5_1: Level = {
  id: '5-1',
  name: 'Cell Assembly',
  type: 'builder',
  specimens: [],
  objectives: [
    { type: 'construct_cell', components: ['membrane', 'cytoplasm', 'nucleus', 'organelles'] },
    { type: 'cell_functions_test', pass: true },
  ],
  challenge: 'construct_simple_cell',
  components: ['membrane', 'cytoplasm', 'nucleus', 'mitochondria', 'ribosome'],
  mechanics: ['drag_component', 'place_component', 'test_cell'],
};

const LEVEL_5_2: Level = {
  id: '5-2',
  name: 'Tissue Formation',
  type: 'builder',
  specimens: [],
  objectives: [
    { type: 'arrange_cells_into_tissue', cellCount: 6 },
    { type: 'tissue_pattern_match', accuracy: 0.8 },
  ],
  challenge: 'arrange_cells_into_tissue',
  tissueTypes: ['epithelial', 'muscle', 'connective'],
  mechanics: ['select_cell_type', 'place_cell', 'arrange_pattern'],
};

const LEVEL_5_3: Level = {
  id: '5-3',
  name: 'Organ Construction',
  type: 'builder',
  specimens: [],
  objectives: [
    { type: 'combine_tissues', tissueCount: 3 },
    { type: 'organ_functions_test', pass: true },
  ],
  challenge: 'combine_tissues_into_organ',
  organs: ['simple_leaf', 'simple_muscle'],
  mechanics: ['layer_tissues', 'connect_tissues', 'test_organ'],
};

const LEVEL_5_4: Level = {
  id: '5-4',
  name: 'Final Challenge',
  type: 'builder',
  specimens: [],
  objectives: [
    { type: 'build_functional_organism', pass: true },
    { type: 'all_life_functions_present', count: 7 },
  ],
  challenge: 'build_functional_organism',
  requirements: [
    'performs_all_life_functions',
    'correct_cellular_organization',
    'passes_life_test',
  ],
  mechanics: ['full_builder', 'life_function_test', 'hierarchy_validation'],
};

const CHAPTER_5_BOSS: BossChallenge = {
  name: 'Master Builder',
  description: 'Build a complete multicellular organism from scratch that performs all seven life functions!',
  specimens: [],
  challenges: [
    'Build an organism that can metabolize nutrients',
    'Ensure your organism can respond to stimuli',
    'Create tissues that work together',
    'Pass the final life function test',
  ],
  difficulty: 'hard',
};

export const CHAPTER_5: Chapter = {
  id: 'ch5_building_life',
  title: 'Building Life',
  learningObjectives: [
    'Understand cellular organization in multicellular organisms',
    'Learn the hierarchy: cells → tissues → organs → systems',
    'Apply knowledge to construct functional organisms',
    'Recognize how specialization enables complexity',
  ],
  levels: [LEVEL_5_1, LEVEL_5_2, LEVEL_5_3, LEVEL_5_4],
  unlockRequirement: {
    type: 'chapter_complete',
    targetId: 'ch4_cell_theory',
  },
  bossChallenge: CHAPTER_5_BOSS,
};

// ============================================================================
// ALL CHAPTERS
// ============================================================================

export const CHAPTERS: Chapter[] = [
  CHAPTER_1,
  CHAPTER_2,
  CHAPTER_3,
  CHAPTER_4,
  CHAPTER_5,
];

/**
 * All chapters indexed by ID
 */
export const CHAPTERS_BY_ID: Record<string, Chapter> = {
  ch1_discovering_cells: CHAPTER_1,
  ch2_one_or_many: CHAPTER_2,
  ch3_life_functions: CHAPTER_3,
  ch4_cell_theory: CHAPTER_4,
  ch5_building_life: CHAPTER_5,
};

/**
 * Get chapter by ID
 */
export function getChapter(id: string): Chapter | undefined {
  return CHAPTERS_BY_ID[id];
}

/**
 * Get level by ID (searches all chapters)
 * Also handles boss level IDs in format: boss-{chapterNum}
 */
export function getLevel(levelId: string): Level | undefined {
  // Check for boss level format: boss-{chapterNum}
  const bossMatch = levelId.match(/^boss-(\d+)$/);
  if (bossMatch) {
    const chapterNum = parseInt(bossMatch[1]);
    const chapter = CHAPTERS.find((c) => c.id.includes(`ch${chapterNum}`));
    if (chapter?.bossChallenge) {
      // Create a synthetic Level from BossChallenge
      return {
        id: levelId,
        name: chapter.bossChallenge.name,
        type: 'discovery',
        specimens: chapter.bossChallenge.specimens || [],
        objectives: [
          { type: 'classify_correctly', accuracy: 1.0 },
        ],
      };
    }
    return undefined;
  }

  // Regular level lookup
  for (const chapter of CHAPTERS) {
    const level = chapter.levels.find(l => l.id === levelId);
    if (level) return level;
  }
  return undefined;
}

/**
 * Get chapter containing a specific level
 * Also handles boss level IDs in format: boss-{chapterNum}
 */
export function getChapterForLevel(levelId: string): Chapter | undefined {
  // Check for boss level format: boss-{chapterNum}
  const bossMatch = levelId.match(/^boss-(\d+)$/);
  if (bossMatch) {
    const chapterNum = parseInt(bossMatch[1]);
    return CHAPTERS.find((c) => c.id.includes(`ch${chapterNum}`));
  }

  return CHAPTERS.find(chapter =>
    chapter.levels.some(level => level.id === levelId)
  );
}

/**
 * Get all specimens used in a chapter
 */
export function getChapterSpecimens(chapterId: string): string[] {
  const chapter = getChapter(chapterId);
  if (!chapter) return [];

  const specimens = new Set<string>();

  for (const level of chapter.levels) {
    if (level.specimens) {
      level.specimens.forEach(s => specimens.add(s));
    }
  }

  if (chapter.bossChallenge?.specimens) {
    chapter.bossChallenge.specimens.forEach(s => specimens.add(s));
  }

  return Array.from(specimens);
}

/**
 * Get all objective types used across all levels
 */
export function getAllObjectiveTypes(): string[] {
  const types = new Set<string>();

  for (const chapter of CHAPTERS) {
    for (const level of chapter.levels) {
      for (const objective of level.objectives) {
        types.add(objective.type);
      }
    }
  }

  return Array.from(types);
}

/**
 * Check if a level has been unlocked based on requirements
 * @param levelId The level to check
 * @param completedLevels Array of completed level IDs
 * @param completedChapters Array of completed chapter IDs
 */
export function isLevelUnlocked(
  levelId: string,
  completedLevels: string[],
  completedChapters: string[]
): boolean {
  const chapter = getChapterForLevel(levelId);
  if (!chapter) return false;

  // First level in first chapter is always unlocked
  if (chapter.levels[0].id === levelId && chapter.id === CHAPTER_1.id) {
    return true;
  }

  // Check if previous level in same chapter is complete
  const levelIndex = chapter.levels.findIndex(l => l.id === levelId);
  if (levelIndex > 0) {
    const previousLevel = chapter.levels[levelIndex - 1];
    if (!completedLevels.includes(previousLevel.id)) {
      return false;
    }
  }

  // Check chapter unlock requirement
  if (chapter.unlockRequirement) {
    const req = chapter.unlockRequirement;

    switch (req.type) {
      case 'chapter_complete':
        if (req.targetId && !completedChapters.includes(req.targetId)) {
          return false;
        }
        break;

      case 'level_complete':
        if (req.targetId && !completedLevels.includes(req.targetId)) {
          return false;
        }
        break;

      // Other requirement types would be handled here
    }
  }

  return true;
}

/**
 * Calculate progress percentage for a chapter
 * @param chapterId The chapter ID
 * @param completedLevels Array of completed level IDs
 * @param bossDefeated Whether the boss challenge is complete
 */
export function getChapterProgress(
  chapterId: string,
  completedLevels: string[],
  bossDefeated: boolean
): number {
  const chapter = getChapter(chapterId);
  if (!chapter) return 0;

  const totalLevels = chapter.levels.length + (chapter.bossChallenge ? 1 : 0);
  let completed = 0;

  for (const level of chapter.levels) {
    if (completedLevels.includes(level.id)) {
      completed++;
    }
  }

  if (chapter.bossChallenge && bossDefeated) {
    completed++;
  }

  return completed / totalLevels;
}
